import { useRef, useState } from 'react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { saveAs } from 'file-saver';
import {
  EventUserEntity,
  EventUserStatus,
  EventUserStatusLabel,
  useFindEventQrQuery,
  useFindEventUsersQuery,
  useFindOneEventQuery,
  useUpdateEventUserMutation,
} from 'src/store/eventApi';
import { Panel } from 'src/ui/Panel';
import { Card } from 'src/ui/Card';
import { Button } from 'src/ui/Button';
import { Badge } from 'src/ui/Badge';
import { Spinner } from 'src/ui/Spinner';
import { EmptyState } from 'src/ui/EmptyState';
import { getAttendeeDisplayName } from 'src/util';
import { NEW_EVENT_ID } from './index';

/**
 * Draws the QR onto a taller canvas with the event's name underneath, so a
 * downloaded code is identifiable once it's one of five PNGs in a folder or
 * pasted into a chat. Sized off the QR's own width, so it keeps working if the
 * export resolution changes.
 *
 * The name is one line: it sits under a code people scan, not a poster. A long
 * name shrinks to fit and, past the point where shrinking would make it
 * unreadable, is cut with an ellipsis.
 */
const CAPTION_MAX_FONT = 0.075; // of the QR's width
const CAPTION_MIN_FONT = 0.042;
const CAPTION_SIDE_MARGIN = 0.08;

function withCaption(
  source: HTMLCanvasElement,
  title: string
): HTMLCanvasElement {
  const width = source.width;
  const band = Math.round(width * 0.16);
  const out = document.createElement('canvas');
  out.width = width;
  out.height = source.height + band;

  const ctx = out.getContext('2d');
  if (!ctx) return source;

  // The QR canvas paints its own white quiet zone; the band has to match it,
  // or the caption sits on transparent pixels that render black in some
  // viewers.
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(source, 0, 0);

  const name = title.trim();
  if (!name) return out;

  const maxWidth = width * (1 - CAPTION_SIDE_MARGIN * 2);
  // A CJK-capable stack: an event named 秋日茶会 is the common case here, and
  // the browser silently falls back to a Latin-only face otherwise.
  const font = (px: number) =>
    `600 ${px}px "Noto Sans SC", "PingFang SC", "Hiragino Sans GB", ` +
    `"Microsoft YaHei", system-ui, sans-serif`;

  let size = Math.round(width * CAPTION_MAX_FONT);
  const minSize = Math.round(width * CAPTION_MIN_FONT);
  ctx.font = font(size);
  while (ctx.measureText(name).width > maxWidth && size > minSize) {
    size -= 2;
    ctx.font = font(size);
  }

  let text = name;
  while (ctx.measureText(text).width > maxWidth && text.length > 1) {
    text = text.slice(0, -1);
  }
  if (text !== name) text = `${text.slice(0, -1)}…`;

  ctx.fillStyle = '#111111';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, width / 2, source.height + band / 2);

  return out;
}

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const STATUS_COLOR: Record<EventUserStatus, string> = {
  [EventUserStatus.REGISTERED]: 'gray',
  [EventUserStatus.CONFIRMED]: 'green',
  [EventUserStatus.CANCELLED]: 'red',
  [EventUserStatus.ATTENDED]: 'blue',
  [EventUserStatus.INVITED]: 'purple',
  [EventUserStatus.WAITLISTED]: 'orange',
};

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

interface SignupProps {
  eventId: string;
}

const Signup = ({ eventId }: SignupProps) => {
  const isNew = eventId === NEW_EVENT_ID;
  const skip = isNew;

  const { data: event } = useFindOneEventQuery(eventId, { skip });
  const {
    data: qr,
    isLoading: qrLoading,
    isError: qrError,
  } = useFindEventQrQuery(eventId, { skip });
  const { data: attendees } = useFindEventUsersQuery(eventId, { skip });
  const [updateEventUser, updateStatus] = useUpdateEventUserMutation();

  const [copied, setCopied] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [promotingId, setPromotingId] = useState<number | null>(null);

  if (isNew) {
    return (
      <div
        style={{
          padding: 16,
          color: 'var(--text-muted)',
          fontSize: 'var(--fs-sm)',
        }}
      >
        Save the event first to generate a sign-up QR.
      </div>
    );
  }

  const onCopyLink = () => {
    if (!qr) return;
    navigator.clipboard.writeText(qr.payload);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const onDownloadPng = () => {
    const source = canvasRef.current;
    if (!qr || !source) return;
    withCaption(source, qr.title).toBlob((blob) => {
      if (blob) saveAs(blob, `${slugify(qr.title)}-signup-qr.png`);
    }, 'image/png');
  };

  const onOpenPoster = () => {
    window.open(
      `/events/${eventId}/qr-poster`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const onPromote = async (eventUserId: number) => {
    setPromotingId(eventUserId);
    try {
      await updateEventUser({
        eventId,
        eventUserId,
        status: EventUserStatus.CONFIRMED,
      }).unwrap();
    } finally {
      setPromotingId(null);
    }
  };

  const signups = (attendees ?? [])
    .filter((eu): eu is EventUserEntity & { selfEnrolledAt: string } =>
      Boolean(eu.selfEnrolledAt)
    )
    .sort(
      (a, b) =>
        new Date(b.selfEnrolledAt).getTime() -
        new Date(a.selfEnrolledAt).getTime()
    );

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 16 }}>
      <Panel
        title="Sign-up QR"
        icon="qr-code"
        eyebrow
        headerRight={
          qr && (
            <span className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>
              {qr.confirmedCount} signed up · {qr.waitlistCount} waitlisted
            </span>
          )
        }
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 340px) 1fr',
            gap: 16,
            alignItems: 'start',
          }}
        >
          {/* Left — QR + distribution tools */}
          <Card pad>
            {qrLoading ? (
              <div
                className="row"
                style={{ justifyContent: 'center', padding: 40 }}
              >
                <Spinner />
              </div>
            ) : qrError || !qr ? (
              <EmptyState
                icon="alert-triangle"
                title="Couldn't load QR code"
                desc="Try reopening this tab."
              />
            ) : (
              <div
                className="col gap-3"
                style={{ alignItems: 'center', textAlign: 'center' }}
              >
                <QRCodeSVG
                  value={qr.payload}
                  size={240}
                  level="M"
                  includeMargin
                />
                <QRCodeCanvas
                  ref={canvasRef}
                  value={qr.payload}
                  size={1024}
                  level="M"
                  includeMargin
                  style={{ position: 'absolute', left: -9999, top: -9999 }}
                />
                <span
                  className="mono text-secondary truncate"
                  style={{ fontSize: 'var(--fs-xs)', maxWidth: '100%' }}
                >
                  {qr.payload}
                </span>
                <div
                  className="row gap-2"
                  style={{ flexWrap: 'wrap', justifyContent: 'center' }}
                >
                  <Button size="sm" leftIcon="copy" onClick={onCopyLink}>
                    {copied ? 'Copied' : 'Copy link'}
                  </Button>
                  <Button size="sm" leftIcon="download" onClick={onDownloadPng}>
                    Download PNG
                  </Button>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon="external-link"
                  onClick={onOpenPoster}
                >
                  Open printable poster
                </Button>
              </div>
            )}
          </Card>

          {/* Right — live sign-ups */}
          <Card pad>
            <div className="col gap-3">
              <div
                className="row gap-3"
                style={{ fontSize: 'var(--fs-sm)', flexWrap: 'wrap' }}
              >
                <span className="row gap-1">
                  <span className="text-secondary">Signed up</span>
                  <strong>
                    {qr?.confirmedCount ?? 0}
                    {event?.capacity ? ` / ${event.capacity}` : ''}
                  </strong>
                </span>
                <span className="row gap-1">
                  <span className="text-secondary">Waitlist</span>
                  <strong>{qr?.waitlistCount ?? 0}</strong>
                </span>
              </div>

              {signups.length === 0 ? (
                <EmptyState
                  icon="qr-code"
                  title="No sign-ups yet"
                  desc="Scans from the QR code will show up here as people enroll."
                />
              ) : (
                <div className="col gap-2">
                  {signups.map((eu) => (
                    <SignupRow
                      key={eu.id}
                      eu={eu}
                      onPromote={() => onPromote(eu.id)}
                      promoting={
                        promotingId === eu.id && updateStatus.isLoading
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      </Panel>
    </div>
  );
};

interface SignupRowProps {
  eu: EventUserEntity & { selfEnrolledAt: string };
  onPromote: () => void;
  promoting: boolean;
}

const SignupRow = ({ eu, onPromote, promoting }: SignupRowProps) => {
  const displayName = getAttendeeDisplayName(eu);
  const status = eu.status as EventUserStatus;

  return (
    <div
      className="row gap-2"
      style={{
        alignItems: 'center',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: '8px 10px',
      }}
    >
      <div className="grow" style={{ minWidth: 0 }}>
        <div
          className="truncate"
          style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}
        >
          {displayName || '(No name)'}
        </div>
        <div className="text-muted" style={{ fontSize: 'var(--fs-2xs)' }}>
          {fmtDateTime(eu.selfEnrolledAt)}
        </div>
      </div>
      <Badge color={STATUS_COLOR[status]} size="sm">
        {EventUserStatusLabel[status]}
      </Badge>
      {status === EventUserStatus.WAITLISTED && (
        <Button size="sm" loading={promoting} onClick={onPromote}>
          Promote
        </Button>
      )}
    </div>
  );
};

export default Signup;
